/**
 * The line that shows where the music is, and the two things it does besides being drawn.
 *
 * Every page that plays a score behaves the way a notation program does: a line crosses the
 * bar as it sounds, a click on the page moves the music there -- and keeps it playing if it
 * was -- and when the music runs onto a system out of view, the desk scrolls to it. Both the
 * Arrange panels and the Editor mount this, so the three behave the same in both. Turning to
 * the next *sheet* is `use-shown-page.ts`, since only one is on the desk at a time.
 *
 * The desk scrolls when the *music* moves to a new system, not whenever the line is out of
 * view: a reader who scrolls away to look at something else while it plays is not pulled
 * back on every frame, only when the music reaches the next line of the score.
 */

import { useCallback, useLayoutEffect, useMemo, useRef } from "react";
import type { RefObject } from "react";
import { momentAt, reachedAt } from "@app/score-editor";
import type { Hit, Moment, Score } from "@app/score-editor";
import type { Playback } from "@app/score-editor/react";

export interface Playhead {
  /** Where to draw the line, or `null` when the music is neither playing nor paused mid-way. */
  readonly at: Moment | null;
  /** Move the music to what was clicked, playing on if it was playing. */
  jump(hit: Hit | null): void;
}

/** `score` is the one the schedule was made from; its bars are the drawn bars. */
export function usePlayhead(score: Score, playback: Playback): Playhead {
  const { playing, position, schedule, start, seek } = playback;

  // Stopped, the position stands at the start and a line there would only be in the way.
  const at = useMemo(
    () => (playing || position > start ? momentAt(score, schedule, position) : null),
    [playing, position, schedule, score, start],
  );

  // Seeking a running player moves it and it plays on from there; `play` would do nothing,
  // since the player is already running.
  const jump = useCallback(
    (hit: Hit | null): void => {
      if (hit === null) return;
      const seconds = reachedAt(score, schedule, hit.event);
      if (seconds !== null) seek(seconds);
    },
    [schedule, score, seek],
  );

  return { at, jump };
}

/** Room left above a system the page turns to, in CSS pixels. */
const LEAD = 24;

/** The nearest element at or above `from` that scrolls vertically, or `null`. */
export function scrollerOf(from: HTMLElement): HTMLElement | null {
  for (let at: HTMLElement | null = from; at !== null; at = at.parentElement) {
    const { overflowY } = getComputedStyle(at);
    const scrolls = overflowY === "auto" || overflowY === "scroll";
    if (scrolls && at.scrollHeight > at.clientHeight) return at;
  }
  return null;
}

/** What the last look at the line saw. */
interface Seen {
  readonly line: Element;
  /** The line's top on its page, which changes only when the music reaches a new system. */
  readonly top: number;
  /** Whether it was inside the frame's width. */
  readonly across: boolean;
}

/**
 * Turn the page to the playhead line drawn inside `frame` while `following`.
 *
 * Vertically, the nearest scrolling ancestor is moved so the line's system sits at the top.
 * Horizontally -- a page zoomed wider than its frame -- the frame itself is moved when the
 * line runs past its edge. It runs after every render, since the line moves on every frame.
 */
export function useFollowPlayhead(frame: RefObject<HTMLElement>, following: boolean): void {
  const seen = useRef<Seen | null>(null);

  useLayoutEffect(() => {
    const root = frame.current;
    const line = root?.querySelector<HTMLElement>("[data-score-playhead]") ?? null;
    if (!following || root === null || line === null) {
      seen.current = null;
      return;
    }

    const was = seen.current;
    const turned = was === null || was.line !== line || Math.abs(was.top - line.offsetTop) > 1;
    const box = line.getBoundingClientRect();

    const scroller = scrollerOf(root);
    if (turned && scroller !== null) {
      const view = scroller.getBoundingClientRect();
      if (box.top < view.top || box.bottom > view.bottom) {
        scroller.scrollTop += box.top - view.top - LEAD;
      }
    }

    const edges = root.getBoundingClientRect();
    let across = box.left >= edges.left && box.right <= edges.right;
    if (!across && (turned || was?.across === true)) {
      root.scrollLeft += box.left - edges.left - root.clientWidth / 4;
      across = true;
    }

    seen.current = { line, top: line.offsetTop, across };
  });
}
